/**
 * Time/date helpers. Everything that names a DAY takes the shop's IANA zone.
 *
 * ⚠️ This file used to say day ranges "use the browser-local day (≈ org tz for a
 * single-location shop — precise org-tz day boundaries are a later refinement)". Ticket 023
 * is that refinement, and the approximation was worse than it sounded: the browser's day is
 * the day of whoever is HOLDING the device. An owner checking tonight's board from a hotel
 * in Los Angeles saw a different "today" than the dispatcher two desks from the vans, and
 * neither matched what the server put in the reports.
 *
 * ⭐ There is no default zone here on purpose. `todayISO`, `dayRange` and `dayLabel` all
 * REQUIRE a `tz`, so a screen that has not yet loaded `org.info` cannot silently render New
 * York time — it has to say it is still loading. That is the whole fix for the six pages
 * that carried `?? 'America/New_York'` and never checked the state behind it.
 */

import { addDaysYmd, localDateOf, resolveLocalDateRange } from '@servwell/shared';

export function timeInTz(d: Date | string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(d));
}

/**
 * Minutes since midnight AT THE SHOP — what the timeline board positions a card by.
 *
 * ⭐ Same instrument as `timeInTz` above, returning the number instead of the string. The
 * board needs "how far into the shop's day is this", and reading the browser's own hours off
 * a `Date` answers a different question: a dispatcher on a laptop in Denver would see a
 * Boston shop's 9am job sitting at 7am on the grid.
 *
 * ⚠️ `hourCycle: 'h23'` is load-bearing. `hour: '2-digit'` alone yields "24" for midnight in
 * some locales, which reads as 1440 minutes and puts a midnight visit off the end of the
 * board instead of at its start.
 */
export function minutesInTz(d: Date | string, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(d));
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  return n('hour') * 60 + n('minute');
}

/**
 * Noon at the SHOP, in minutes since midnight — the boundary that decides whether a promised
 * window has been broken, and the AM/PM divider the week board's load bars split on.
 *
 * 🔴 **This number is a RULING, not a derivation.** `windowLabel` deliberately refuses to
 * print hour boundaries because nothing in the schema says when a shop's morning ends, and
 * inventing one would tell a tech a promise the office never made. That objection is
 * answered rather than bypassed: Giuliano ruled on 2026-08-27 that **anything after 12:00 is
 * past the morning**. So the boundary now exists as a decision, recorded here with its date,
 * in one place, rather than being spread across the components that need it.
 *
 * ⚠️ Still not printed. The card says "Morning", never "8am–12pm" — the boundary decides
 * whether the promise HOLDS; it is not itself a promise to show anybody.
 */
export const MORNING_ENDS_AT = 12 * 60;

/** Today's calendar date at the SHOP, as `YYYY-MM-DD`. */
export function todayISO(tz: string): string {
  return localDateOf(new Date(), tz);
}

/**
 * Add N days to a `YYYY-MM-DD`. Zone-free by construction — delegates to the shared helper,
 * which anchors at UTC noon so no offset or DST change can move the date.
 */
export function addDaysISO(iso: string, days: number): string {
  return addDaysYmd(iso, days);
}

/** "Today", or "Mon, Aug 3" — the "today" being the shop's, not the reader's. */
export function dayLabel(iso: string, tz: string): string {
  const d = new Date(`${iso}T12:00:00`);
  if (iso === todayISO(tz)) return 'Today';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/**
 * The half-open instant window `[from, to)` covering the shop-local day of `iso`.
 *
 * Was the BROWSER-local day, which is the bug: a dispatcher on the road, a tech whose phone
 * picked up a border zone, or anyone with the wrong device clock got a board that started
 * and ended at the wrong hour. `resolveLocalDateRange` is the same DST-correct helper the
 * server ranges reports with, so both ends of the wire now cut the day at the same instant.
 */
export function dayRange(iso: string, tz: string): { from: Date; to: Date } {
  const { startUtc, endUtc } = resolveLocalDateRange(iso, iso, tz);
  return { from: startUtc, to: endUtc };
}

/**
 * Render a DATE-ONLY value ('2026-07-31' — a Postgres `date` column) in the local
 * calendar. `new Date('2026-07-31')` parses as UTC midnight, so anywhere west of
 * Greenwich it renders as the 30th: a start date, a due date and an invoice date all
 * silently a day early. Anchoring at noon puts it safely inside the local day for every
 * real-world offset.
 *
 * ⭐ Takes no `tz`, and should not. A `date` is a calendar day with no instant attached —
 * an invoice dated the 31st is the 31st wherever it is read. Same reasoning as the server's
 * `dateRangeWhere`. Timestamps (`timestamptz`) do NOT come through here — they carry their
 * own instant, and belong in `timeInTz`.
 */
export function dateOnly(iso: string, opts: Intl.DateTimeFormatOptions = {}): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, opts);
}

/**
 * The calendar DAY a timestamp fell on AT THE SHOP, rendered.
 *
 * The missing member of this file's family, and its absence showed: a `done_at`
 * (`timestamptz`) formatted with a bare `new Date(x).toLocaleDateString()` is the day it
 * was in the READER's zone, so an owner checking from a hotel and the dispatcher two
 * desks from the vans disagree about which day a service was completed — the exact defect
 * the docblock at the top of this file describes, one column type over. `timeInTz` covers
 * the time half; this covers the day.
 */
export function dayInTz(d: Date | string, tz: string): string {
  return dateOnly(localDateOf(new Date(d), tz));
}

/** Sortable form of the same value — same noon anchor, so sort and label agree. */
export function dateOnlyMs(iso: string): number {
  return new Date(`${iso}T12:00:00`).getTime();
}

/**
 * Whole days from `a` to `b`, both `YYYY-MM-DD`.
 *
 * Rounded, because a week containing a DST change is 167 or 169 hours, not 168 — and this
 * number is what a bulk reschedule sends the server. An unrounded 6.96 truncates to 6 and
 * moves a whole day of work to the wrong day, twice a year.
 */
export function daysBetweenISO(a: string, b: string): number {
  return Math.round((dateOnlyMs(b) - dateOnlyMs(a)) / 86_400_000);
}

/**
 * Words joined as a sentence says them: "a", "a and b", "a, b, and c". For lists a person
 * reads in a line of text ("Still needed: …", "Reminders go out …"), never a comma-joined
 * array. 'en-US', the locale every other formatter here names.
 */
export function listOf(parts: readonly string[]): string {
  return new Intl.ListFormat('en-US', { style: 'long', type: 'conjunction' }).format(
    parts,
  );
}

/**
 * A day of the month as a person says it: "1st", "2nd", "3rd", "11th", "22nd", "28th". Where a
 * date reads "on the 31st" (a repeating contract's day) or "to the 28th" (a month in
 * progress on the Revenue chart).
 */
export function ordinal(n: number): string {
  const suffix =
    n % 100 >= 11 && n % 100 <= 13
      ? 'th'
      : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th');
  return `${n}${suffix}`;
}

/**
 * How long from `from` to `to`, counted between the MINUTES the screen prints ("8:07" to
 * "9:02" is 55 min, whatever the seconds): "55 min", "1 h 47 min", "2 h". Never negative.
 *
 * ⚠️ REAL elapsed time, not a wall-clock subtraction, and on purpose: on a spring-forward day
 * a visit in at 1:30 and out at 3:30 took ONE hour, and that is what it says (a fall-back
 * day's 1:30 to 1:30 an hour later likewise reads 1 h, not 0). The time a tech worked is
 * what the office reads it for; the clock's jump is not work. Both ends are UTC instants, so
 * the zone never enters the sum.
 */
export function durationLabel(from: Date | string, to: Date | string): string {
  const minutes = Math.max(
    0,
    Math.floor(new Date(to).getTime() / 60_000) -
      Math.floor(new Date(from).getTime() / 60_000),
  );
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/**
 * A booked start on the shop's clock, with its date when it is not on `date` (the shop's
 * `YYYY-MM-DD`): "9:00 AM", or "Oct 4, 3:00 PM" for a visit booked yesterday and worked
 * today (the crew map's day is wider than the booked day).
 */
export function bookedAt(at: Date | string | null, tz: string, date: string): string {
  if (at === null) return 'Not booked';
  const time = timeInTz(at, tz);
  const day = localDateOf(new Date(at), tz);
  return day === date
    ? time
    : `${dateOnly(day, { month: 'short', day: 'numeric' })}, ${time}`;
}
